// src/features/facturas/FacturaDetallePag.jsx

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api/axiosConfig";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function FacturaDetallePag() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [factura, setFactura] = useState(null);
  const [cargando, setCargando] = useState(true);

  // ticket e impresión
  const [ticketFactura, setTicketFactura] = useState("");
  const [mostrandoConfirmacion, setMostrandoConfirmacion] = useState(false);

  useEffect(() => {
    cargarFactura();
  }, [id]);

  const cargarFactura = async () => {
    try {
      const res = await api.get(`/facturas/${id}`);
      setFactura(res.data);
    } catch (error) {
      console.error(error);
      toast.error("❌ No se pudo obtener la factura");
    } finally {
      setCargando(false);
    }
  };

  const obtenerTicketFactura = async () => {
    try {
      const res = await api.get(`/facturas/${id}/ticket`);
      setTicketFactura(res.data);
      return res.data;
    } catch (error) {
      console.error("Error obteniendo ticket de factura:", error);
      toast.error("❌ No se pudo generar el ticket de la factura");
      return null;
    }
  };

  const imprimirFactura = async () => {
    try {
      const ticket = ticketFactura || (await obtenerTicketFactura());
      if (!ticket) return;

      await api.post("/imprimir", {
        impresora: "POS-80C",
        contenido: ticket,
      });

      toast.success("🖨️ Factura enviada a impresión");
    } catch (error) {
      console.error("Error al imprimir la factura:", error);
      toast.error("❌ No se pudo imprimir la factura");
    }
  };

  if (cargando) {
    return (
      <div className="mt-10 flex flex-col items-center justify-center text-slate-700 dark:text-slate-100">
        <ToastContainer position="top-right" autoClose={3000} />
        <div className="w-8 h-8 border-2 border-slate-400 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-sm">Cargando factura...</p>
      </div>
    );
  }

  if (!factura) {
    return (
      <div className="mt-10 flex flex-col items-center justify-center text-slate-500 dark:text-slate-300">
        <ToastContainer position="top-right" autoClose={3000} />
        <p className="text-sm">Factura no encontrada.</p>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-400 dark:border-slate-600 text-xs md:text-sm text-slate-700 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          ⬅️ Volver
        </button>
      </div>
    );
  }

  return (
    <div className="mt-4 px-3 md:px-6">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Card principal */}
      <div className="bg-white/95 dark:bg-slate-900/90 text-slate-900 dark:text-slate-100 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-5 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/70">
          <div className="flex items-center gap-2">
            <span className="text-amber-500 dark:text-amber-300">🧾</span>
            <div>
              <h2 className="text-base md:text-lg font-semibold text-slate-800 dark:text-slate-100">
                Detalle de Factura #{factura.id}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Revisa la información de la factura y los productos cobrados.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 justify-end">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-400 dark:border-slate-600 text-xs md:text-sm text-slate-700 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              ⬅️ Volver
            </button>

            <button
              type="button"
              onClick={() => setMostrandoConfirmacion(true)}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium border border-purple-600 bg-purple-600 text-white hover:bg-purple-700 transition-colors"
            >
              🖨️ Imprimir
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Datos generales */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs sm:text-sm">
            <div className="space-y-1.5">
              <p>
                <span className="font-semibold text-slate-700 dark:text-slate-200">
                  Fecha de emisión:
                </span>{" "}
                {factura.fechaEmision
                  ? new Date(factura.fechaEmision).toLocaleString()
                  : "—"}
              </p>
              <p>
                <span className="font-semibold text-slate-700 dark:text-slate-200">
                  Método de pago:
                </span>{" "}
                {factura.metodoPago || "—"}
              </p>
              <p className="flex items-center gap-2">
                <span className="font-semibold text-slate-700 dark:text-slate-200">
                  Estado:
                </span>
                <span
                  className={`inline-flex items-center justify-center px-2.5 py-1 rounded-full text-[11px] font-semibold border gap-1 ${
                    factura.pago
                      ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/60"
                      : "bg-amber-500/15 text-amber-300 border-amber-500/60"
                  }`}
                >
                  {factura.pago ? "Pagado" : "Pendiente"}
                </span>
              </p>
              <p>
                <span className="font-semibold text-slate-700 dark:text-slate-200">
                  Total:
                </span>{" "}
                ${factura.total?.toLocaleString() || "0"}
              </p>
            </div>

            <div className="space-y-1.5">
              <p>
                <span className="font-semibold text-slate-700 dark:text-slate-200">
                  Pedido:
                </span>{" "}
                #{factura.pedido?.id || "—"}
              </p>
              <p>
                <span className="font-semibold text-slate-700 dark:text-slate-200">
                  Mesa:
                </span>{" "}
                {factura.pedido?.mesa?.numero || "—"}
              </p>
              <p>
                <span className="font-semibold text-slate-700 dark:text-slate-200">
                  Mesero:
                </span>{" "}
                {factura.pedido?.mesero?.nombre || "—"}
              </p>
              <p>
                <span className="font-semibold text-slate-700 dark:text-slate-200">
                  Descripción del pedido:
                </span>{" "}
                {factura.pedido?.descripcion || "—"}
              </p>
            </div>
          </div>

          {/* Productos facturados */}
          <div className="mt-2">
            <h3 className="text-sm font-semibold text-sky-500 dark:text-sky-300 mb-2 flex items-center gap-2">
              <span>🧩</span>
              <span>Productos facturados</span>
            </h3>

            {factura.detalles?.length > 0 ? (
              <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                <table className="w-full min-w-[600px] md:min-w-full text-xs sm:text-sm">
                  <thead className="bg-slate-900 text-slate-100">
                    <tr>
                      <th className="px-3 py-2 text-center font-semibold">
                        #
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        Producto
                      </th>
                      <th className="px-3 py-2 text-center font-semibold">
                        Cantidad
                      </th>
                      <th className="px-3 py-2 text-center font-semibold">
                        Precio unitario
                      </th>
                      <th className="px-3 py-2 text-center font-semibold">
                        Subtotal
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {factura.detalles.map((d, idx) => (
                      <tr
                        key={idx}
                        className="odd:bg-white even:bg-slate-50 dark:odd:bg-slate-900/60 dark:even:bg-slate-900/40"
                      >
                        <td className="px-3 py-2 text-center align-middle">
                          {idx + 1}
                        </td>
                        <td className="px-3 py-2 text-left align-middle">
                          {d.producto?.nombre || "—"}
                        </td>
                        <td className="px-3 py-2 text-center align-middle">
                          {d.cantidad}
                        </td>
                        <td className="px-3 py-2 text-center align-middle">
                          ${d.precioUnitario?.toLocaleString() || "0"}
                        </td>
                        <td className="px-3 py-2 text-center align-middle font-semibold">
                          ${d.subtotal?.toLocaleString() || "0"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs sm:text-sm text-center text-slate-500 dark:text-slate-400 mt-2">
                No hay productos asociados a esta factura.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Modal confirmación impresión */}
      {mostrandoConfirmacion && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60">
          <div className="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-2xl shadow-2xl border border-slate-300 dark:border-slate-700 max-w-sm w-[90%] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
              <h3 className="text-sm md:text-base font-semibold flex items-center gap-2">
                🖨️ Confirmar impresión
              </h3>
              <button
                onClick={() => setMostrandoConfirmacion(false)}
                className="text-xs px-2 py-1 rounded-lg border border-slate-400 dark:border-slate-600
                           bg-slate-100 dark:bg-slate-800
                           text-slate-700 dark:text-slate-200
                           hover:bg-slate-200 dark:hover:bg-slate-700 transition"
              >
                Cerrar
              </button>
            </div>

            {/* Body */}
            <div className="px-4 py-4 text-xs sm:text-sm space-y-2">
              <p className="text-slate-700 dark:text-slate-200">
                ¿Deseas imprimir la factura #{factura.id}?
              </p>
              <p className="text-slate-500 dark:text-slate-400 text-xs">
                Asegúrate de que la impresora POS esté encendida y conectada.
              </p>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2 bg-slate-50 dark:bg-slate-800">
              <button
                onClick={() => setMostrandoConfirmacion(false)}
                className="px-3 py-1.5 text-xs sm:text-sm rounded-lg
                           border border-slate-400 dark:border-slate-600
                           bg-slate-100 dark:bg-slate-800
                           text-slate-700 dark:text-slate-200
                           hover:bg-slate-200 dark:hover:bg-slate-700 transition"
              >
                Cancelar
              </button>

              <button
                onClick={async () => {
                  setMostrandoConfirmacion(false);
                  await imprimirFactura();
                }}
                className="px-3 py-1.5 text-xs sm:text-sm rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-semibold border border-purple-600 transition-colors"
              >
                Confirmar impresión
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FacturaDetallePag;
